import { useEffect, useState, useMemo } from "react";
import { Search, X, Building2, ShieldCheck, Plus, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const ENTITY_TYPE_LABELS = {
  gobierno_federal: "Gob. Federal",
  gobierno_estatal: "Gob. Estatal",
  gobierno_municipal: "Gob. Municipal",
  organismo_publico_descentralizado: "Org. Público",
  privado_persona_fisica: "Privado · Persona Física",
  privado_persona_moral: "Privado · Persona Moral",
};

export default function ClientPicker({
  value,
  onChange,
  label = "Cliente",
  required = false,
  placeholder = "(sin cliente)",
  allowEmpty = true,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  // Cargar el cliente actual (label)
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    api.get(`/contracting-entities/${value}`).then((r) => setSelected(r.data)).catch(() => setSelected(null));
  }, [value]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracting-entities");
      setItems(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) loadAll();
  }, [open]);

  return (
    <div>
      {label !== null && label !== "" && (
        <label className="label-mono block mb-1">{label}{required && " *"}</label>
      )}
      <button
        type="button"
        data-testid="client-picker-trigger"
        onClick={() => setOpen(true)}
        className="input-cyber px-3 py-2 text-sm w-full text-left flex items-center justify-between"
      >
        <span className={selected ? "text-slate-100" : "text-slate-500"}>
          {selected
            ? `${selected.short_name ? selected.short_name + " — " : ""}${selected.name}`
            : placeholder}
        </span>
        <Search className="w-4 h-4 text-slate-400 shrink-0" />
      </button>
      {open && (
        <ClientPickerModal
          items={items}
          loading={loading}
          onClose={() => setOpen(false)}
          onPick={(c) => { onChange(c ? c.id : null); setSelected(c); setOpen(false); }}
          allowEmpty={allowEmpty}
          onCreated={loadAll}
        />
      )}
    </div>
  );
}

function ClientPickerModal({ items, loading, onClose, onPick, allowEmpty, onCreated }) {
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("privado_persona_moral");
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((it) => {
      if (typeFilter && it.type !== typeFilter) return false;
      if (!term) return true;
      return (it.name || "").toLowerCase().includes(term)
        || (it.short_name || "").toLowerCase().includes(term)
        || (it.rfc || "").toLowerCase().includes(term)
        || (it.legal_representative || "").toLowerCase().includes(term);
    });
  }, [items, q, typeFilter]);

  const createQuick = async () => {
    if (!newName.trim()) { toast.error("Falta el nombre"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/contracting-entities", {
        name: newName.trim(), type: newType, default_iva_pct: 16, auditable: false,
      });
      toast.success("Cliente creado");
      setShowCreate(false);
      setNewName("");
      onCreated?.();
      // Auto-seleccionar el recién creado
      onPick(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear");
    } finally { setCreating(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl panel-strong max-h-[85vh] flex flex-col"
        data-testid="client-picker-modal"
      >
        <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="label-mono">// SELECCIONAR CLIENTE</div>
            <h3 className="font-display font-bold text-base text-slate-100">
              {items.length} {items.length === 1 ? "cliente" : "clientes"} en el catálogo
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-3 border-b grid grid-cols-1 md:grid-cols-2 gap-2" style={{ borderColor: "var(--border)" }}>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              autoFocus
              data-testid="client-picker-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, siglas, RFC o representante…"
              className="input-cyber pl-10 pr-3 py-2 text-sm w-full"
            />
          </div>
          <select
            data-testid="client-picker-type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="input-cyber px-3 py-2 text-sm"
          >
            <option value="" style={{ background: "#0a0f1d" }}>Todos los tipos</option>
            {Object.entries(ENTITY_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k} style={{ background: "#0a0f1d" }}>{v}</option>
            ))}
          </select>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1.5">
          {loading ? (
            <div className="text-center text-slate-400 py-6">Cargando…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Building2 className="w-10 h-10 mx-auto mb-2 text-slate-600" />
              {q || typeFilter ? "Sin resultados con esos filtros." : "Aún no hay clientes."}
              <div className="mt-3">
                <button onClick={() => { setShowCreate(true); setNewName(q); }}
                  className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest inline-flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" /> Crear "{q || "nuevo"}"
                </button>
              </div>
            </div>
          ) : (
            filtered.map((it) => (
              <button
                key={it.id}
                type="button"
                data-testid={`client-pick-${it.id}`}
                onClick={() => onPick(it)}
                className="w-full panel p-3 text-left hover:border-cyan-500/60 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="label-mono text-[10px]" style={{ color: "var(--cyan)" }}>
                      {ENTITY_TYPE_LABELS[it.type] || it.type}
                      {it.rfc && <span className="ml-2 text-slate-500">RFC: {it.rfc}</span>}
                    </div>
                    <div className="font-display font-bold text-sm text-slate-100 truncate mt-0.5">
                      {it.short_name && <span className="text-cyan-300">{it.short_name} · </span>}{it.name}
                    </div>
                    {it.legal_representative && (
                      <div className="text-[11px] text-slate-400 mt-0.5">{it.legal_representative}</div>
                    )}
                  </div>
                  {it.auditable && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-sm shrink-0"
                      style={{ background: "rgba(217,70,239,0.15)", color: "var(--magenta)", border: "1px solid rgba(217,70,239,0.3)" }}>
                      <ShieldCheck className="w-3 h-3" /> AUDITABLE
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {showCreate ? (
          <div className="px-5 py-3 border-t" style={{ borderColor: "var(--border)" }}>
            <div className="label-mono mb-2">// CREAR NUEVO CLIENTE</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input data-testid="quick-create-name" value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="Razón social"
                className="input-cyber px-3 py-2 text-sm md:col-span-2" autoFocus />
              <select value={newType} onChange={(e) => setNewType(e.target.value)} className="input-cyber px-3 py-2 text-sm">
                {Object.entries(ENTITY_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k} style={{ background: "#0a0f1d" }}>{v}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => setShowCreate(false)} className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest">Cancelar</button>
              <button data-testid="quick-create-submit" onClick={createQuick} disabled={creating}
                className="btn-primary px-4 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50">
                <Check className="w-3.5 h-3.5" /> {creating ? "Creando…" : "Crear y seleccionar"}
              </button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-3 border-t flex items-center justify-between gap-2" style={{ borderColor: "var(--border)" }}>
            <button onClick={() => setShowCreate(true)}
              className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5"
              style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}>
              <Plus className="w-3.5 h-3.5" /> Crear nuevo
            </button>
            {allowEmpty && (
              <button data-testid="client-pick-empty" onClick={() => onPick(null)}
                className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest">
                Sin cliente
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
